import Vue from 'vue';
import Vuex from 'vuex';
import Vuetify from '@client/plugins/vuetify';
import StyleInjector from '@client/util/styleInjector';
import fromS3 from '@client/util/s3';
import Store from '@client/models/store';
// eslint-disable-next-line import/no-cycle
import cognitoUser from '@client/store/modules/cognitoUser';
import rwOptions from '@client/store/modules/rwOptions';
// eslint-disable-next-line import/no-cycle
import announcements from '@client/store/modules/announcements';
// eslint-disable-next-line import/no-cycle
import designations from '@client/store/modules/designations';
import {Colors} from '@client/util/constants/colors';
import {ClientWindow} from '@client/custom-window';
import {APPLICATION_SETTINGS} from '@client/constants/applicationSettings';
// eslint-disable-next-line import/no-cycle
import {LoginRouteNames} from '@client/routes/login';
// eslint-disable-next-line import/no-cycle
import movemoney from './modules/movemoney';
// eslint-disable-next-line import/no-cycle
import overview from './modules/overview';
// eslint-disable-next-line import/no-cycle
import accountAggregation from './modules/accountAggregation';
import auth from './modules/auth';
// eslint-disable-next-line import/no-cycle
import user from './modules/user';
// eslint-disable-next-line import/no-cycle
import goal from './modules/goal';
import goalList from './modules/goalList';
// eslint-disable-next-line import/no-cycle
import risk from './modules/risk';
// eslint-disable-next-line import/no-cycle
import {CMS} from './modules/cms';
// eslint-disable-next-line import/no-cycle
import onboarding from './modules/onboarding';
// eslint-disable-next-line import/no-cycle
import settings from './modules/settings';
// eslint-disable-next-line import/no-cycle
import account from './modules/account';
import modal from './modules/modal';
import chat from './modules/chat';
// eslint-disable-next-line import/no-cycle
import accountLimits from './modules/accountLimits';
// eslint-disable-next-line import/no-cycle
import accountList from './modules/accountList';
// eslint-disable-next-line import/no-cycle
import activity from './modules/activity';
// eslint-disable-next-line import/no-cycle
import transfers from './modules/transfers';
// eslint-disable-next-line import/no-cycle
import liquidation from './modules/liquidation';
import faqs from './modules/faqs';
// eslint-disable-next-line import/no-cycle
import bankAccount from './modules/bankAccount';
// eslint-disable-next-line import/no-cycle
import profile from './modules/profile';

const DEFAULT_FAVICON = '';

declare let window: ClientWindow;

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const stateObj = () => new Store();

const getters = {
  hasDashboardAccess: (rootState): boolean => rootState.user.current && rootState.user.current.dashboard_access === 1,

  isTransferMoneyEnabled: (rootState, rootGetters): boolean =>
    rootGetters.hasDashboardAccess && rootState.settings.canMoveMoney,
  transferDirectionId: (rootState): number => rootState.transfers.transferDetail.transferDirection.transferDirectionId,
  riaName: (state): string => state.riaName,
  riaId: ({riaId}): number => riaId,
  onboardingType: (state): string => state.onboardingType,
};

export const actions = {
  setLoading({commit}, payload): void {
    commit('SET_LOADING', payload);
  },
  async getLayout({dispatch, commit, state, rootGetters}, toState): Promise<void> {
    // If maintenance mode is on we don't want to run `getLayout` because it depends on the database.
    if (rootGetters['rwOptions/isUnderMaintenance']) {
      return;
    }

    if (toState?.includes('errors')) {
      // if this is an error redirect, just let them through
      dispatch('setLoading', false);

      return;
    }

    if (state.isLayoutSet) {
      return;
    }

    let appSettings = null;

    const advLoginRoutes = new Set([LoginRouteNames.AdvLoginPage, LoginRouteNames.ForgotPasswordPage]);

    if (advLoginRoutes.has(toState)) {
      const url = process.env.VUE_APP_ENV === 'local' ? process.env.VUE_APP_HOST : window.location.host;

      appSettings = await dispatch('settings/getAppSettings', {url});
    } else {
      appSettings = APPLICATION_SETTINGS;
      await dispatch('settings/setAppSettings', appSettings);
    }

    commit('SET_ONBOARDING_TYPE', appSettings.onboardingType);

    await dispatch('applyAppSettings', {
      result: appSettings?.riaFeatures,
      riaId: appSettings?.riaFeatures?.riaInformation.riaId,
      toState,
    });
  },

  applyAppSettings({commit, rootState, state}, {result, riaId, toState}) {
    Vuetify.framework.theme.themes.light.primary = `${Colors.principalPrimary}`;

    StyleInjector.inject(StyleInjector.DASHBOARD_TYPE, rootState.settings.ria, toState);

    commit('SET_DEFAULT_STATE', {
      favicon: result.riaLogos.riaFavicon ? fromS3(result.riaLogos.riaFavicon) : DEFAULT_FAVICON,
      riaId,
      riaName: result?.riaInformation.companyLegalName,
    });

    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    setFavicon(state.favicon);

    document.title = result.riaInformation.companyLegalName;
  },
  async resetLayout({commit}): Promise<void> {
    await commit('RESET_LAYOUT');
    StyleInjector.clear();
  },
};

// eslint-disable-next-line func-style
function setFavicon(path): void {
  document.querySelector('#favicon').setAttribute('href', path);
}

export const mutations = {
  SET_DEFAULT_STATE(state, payload): void {
    state.isLayoutSet = true;
    state.favicon = payload.favicon;
    state.riaId = payload.riaId;
    state.riaName = payload.riaName;
  },
  SET_LOADING(state, payload): void {
    state.isLoading = payload;
  },
  RESET_LAYOUT(state): void {
    state.isLayoutSet = false;
  },
  SET_ONBOARDING_TYPE(state, onboardingType): void {
    state.onboardingType = onboardingType;
  },
};

const {VUE_APP_CMS_SPACE, VUE_APP_CMS_ENVIRONMENT, VUE_APP_CMS_ACCESS_TOKEN} = process.env;

const cmsModule = VUE_APP_CMS_SPACE && VUE_APP_CMS_ENVIRONMENT && VUE_APP_CMS_ACCESS_TOKEN ? {CMS} : {};
// modules are separated out right now for conditional CMS loading
// when CMS gets sorted out, that conditional won't be needed
// and we can resume inline store construction

// TODO: remove after CMS is sorted out
const modules = {
  announcements,
  designations,
  movemoney,
  auth,
  user,
  goal,
  goalList,
  risk,
  onboarding,
  settings,
  account,
  modal,
  chat,
  accountLimits,
  activity,
  accountList,
  transfers,
  cognitoUser,
  rwOptions,
  overview,
  accountAggregation,
  liquidation,
  faqs,
  bankAccount,
  profile,
  ...cmsModule,
};

Vue.use(Vuex);

const store = new Vuex.Store({
  state: stateObj,
  getters,
  actions,
  mutations,
  modules,
});

export const useStore = (): Store => store;

export default store;
